import { ReactNode, useState } from 'react';

import { useDisclosure } from '@mantine/hooks';

import { IconX } from '@tabler/icons-react';

import { UseMutationResult } from '@tanstack/react-query';

import {
  ActionIcon,
  Button,
  Group,
  Popover,
  PopoverProps,
  Slider,
  Stack,
  Text,
  Textarea,
} from '@optiro/ui';

import { useCreateIntervalCompletionState } from '@/modules/Planning/hooks/intervals/useIntervalCompletionStates';
import { Interval, IntervalCompletionState } from '@/types/types';

type CompletionMutation = UseMutationResult<
  IntervalCompletionState,
  unknown,
  { percentage: number; notes?: string },
  unknown
>;

interface IntervalCompletionStatePopoverProps {
  intervalId: Interval['id'];
  currentPercentage?: number;
  position?: PopoverProps['position'];
  mutation?: CompletionMutation;
  children: (props: { onClick: () => void; loading: boolean }) => ReactNode;
}

export const IntervalCompletionStatePopover = ({
  intervalId,
  currentPercentage = 0,
  position = 'bottom-start',
  mutation,
  children,
}: IntervalCompletionStatePopoverProps) => {
  const [opened, { close, toggle }] = useDisclosure(false);
  const [percentage, setPercentage] = useState(currentPercentage);
  const [notes, setNotes] = useState('');
  const defaultMutation = useCreateIntervalCompletionState(intervalId);
  const createState = mutation ?? defaultMutation;

  const handleSubmit = () => {
    createState.mutate(
      { percentage, notes: notes.trim() || undefined },
      {
        onSuccess: () => {
          setNotes('');
          close();
        },
      }
    );
  };

  return (
    <Popover
      opened={opened}
      onClose={close}
      position={position}
      withArrow
      withinPortal
      shadow="md"
      width={280}
    >
      <Popover.Target>
        {children({ onClick: toggle, loading: createState.isPending }) as React.ReactElement}
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm">
          <Group justify="space-between" align="center" mb={-4}>
            <Text size="xs" fw={500}>
              Færdiggørelsesgrad — {percentage}%
            </Text>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={close}>
              <IconX size={12} />
            </ActionIcon>
          </Group>
          <div style={{ paddingTop: 4 }}>
            <Slider
              value={percentage}
              onChange={setPercentage}
              min={0}
              max={100}
              step={5}
              color="teal"
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' },
              ]}
              mb="sm"
            />
          </div>
          <Textarea
            label="Noter (valgfrit)"
            placeholder="Tilføj en note om fremdriften..."
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            minRows={2}
            autosize
            size="xs"
          />
          <Button
            size="xs"
            color="teal"
            loading={createState.isPending}
            onClick={handleSubmit}
            fullWidth
          >
            Gem fremdrift
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
